/**
 * preview.js — the customer-facing FULL FRAMED PRODUCT preview (CONTRACT v2,
 * COURSE_CORRECTION C1–C6).
 *
 * The artifact is NOT a bare map window — it is the full framed printed product:
 * outer page / border, margins, bottom detail strip, divider line, title, ship
 * name, journey details (dates / duration / distance), logo slots — with the
 * MAP sitting in its INSET. The route trace + coastline skeleton + PNG morph all
 * play INSIDE the inset; the frame stays constant throughout.
 *
 * LAYERING (the conceptual model):
 *   containerEl
 *     └─ vp-root  (position:absolute; inset:0; container-type:size; flex-centred)
 *          └─ shell  (aspect-ratio:W/H; width:min(100cqw,100cqh*(W/H)); centred — fits BOTH axes)
 *               ├─ frame  <svg viewBox="0 0 printCanvas.w printCanvas.h"> absolutely covering shell
 *          │     • page background rect, divider <line>, title/ship/detail <text>,
 *          │       skeleton-placeholder <rect>s, logo slots — ALL in PRINT-PIXEL space.
 *          └─ inset  <div> positioned at frame.inset (% of printCanvas)
 *                ├─ inset bg
 *                ├─ map PNG  <img>            (fills inset; supersample× of insetDimensions)
 *                ├─ route+skeleton <svg viewBox="0 0 insetDimensions.w insetDimensions.h">
 *                └─ status pill / failure copy (overlay the INSET only)
 *
 * THE SINGLE SCALE (C3): there is exactly ONE conceptual scale,
 *   scale = displayWidth / printCanvas.width,
 * and it is realised ENTIRELY by CSS — the frame SVG's viewBox maps print-px to
 * the shell's rendered size, and the inset's %-based position/size scale with it.
 * There is NO JS scale recompute on resize. Resizing the browser just lets CSS
 * re-fit the aspect-ratio box; everything inside (frame text, divider, inset box,
 * pixelPath, PNG) scales as one unit and stays registered.
 *
 * COORDINATE SPACES:
 *   • The FRAME svg works in PRINT-PIXEL space  (frame.printCanvas).
 *   • The INSET svg works in INSET-PIXEL space   (insetDimensions). pixelPath and
 *     skeleton are ALREADY in inset space (0,0 = inset top-left), so they draw
 *     straight into the inset svg. The PNG fills the inset box and registers to
 *     the pixel (true morph preserved).
 *
 * Drawing mirrors map-preview-harness/src/engine/js/imageGenerator.ts EXACTLY
 * using the frame.layout base_config keys — NO hard-coded layout numbers (C2).
 *
 * Choreography (D12) is reused verbatim from the prior draft: trace pixelPath →
 * skeleton fades in at ~0.66 → ease finish → poll job → morph PNG with the 2.5s
 * floor / 8s ceiling. The failure ladder (§4) is preserved, scoped to the inset.
 *
 * Concealment (D11): the embed never sees lat/lng — only inset-space pixelPath +
 * public skeleton + public frame config.
 */

import axios from "axios";
import { DEFAULTS } from "./defaults.js";
import { bundledDefaultFrame } from "./standardFrame.js";
import { ensureBrandFonts } from "./fonts.js";
import { makeChecklist } from "./checklist.js";
import { coBrandingPrintBox, clampWidthToLogos } from "./branding.js";
import {
  produceDisplayDateRange,
  pluraliseDays,
  clamp,
  traceProgress,
  splitRouteSubpaths,
  subpathToD,
  arcLengthOfPoint,
  buildPortTimeWarp,
  fontFamilyWithFallback,
  measureTextWidth,
  wrapTitleLines,
} from "./animMath.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";
// Relative to apiBaseUrl (the `/api/v3` base). Resolved: `<apiBaseUrl>/style/assets/<file>`.
const STYLE_ASSET_PATH = "/style/assets";

// ----- pure math/formatting helpers (extracted verbatim → animMath.js) -------
// date/pluralise formatting, easing, the trace-progress curve, the antimeridian seam split,
// SVG-path building, port arc-length + time-warp, and the font/title-wrap text helpers. Moved
// out unchanged; imported back here where the frame/animation code uses them.

export function createVoyagePreview(containerEl, options = {}) {
  if (!containerEl || !containerEl.appendChild) {
    throw new Error("createVoyagePreview: first arg must be a DOM element");
  }
  const baseOpts = { ...DEFAULTS, ...options };
  // apiBaseUrl is REQUIRED — there is no default (Task 4c). A missing base means the storefront
  // forgot the option; rather than silently call a fallback (the old localhost mock leaked into the
  // production bundle), we refuse: log a clear console error and drop straight into the neutral
  // "unavailable" state. No network is attempted (loadRecipes/run are gated below).
  const apiConfigured =
    typeof baseOpts.apiBaseUrl === "string" && baseOpts.apiBaseUrl.trim() !== "";
  if (!apiConfigured) {
    console.error(
      "createVoyagePreview: `apiBaseUrl` is required (the /api/v3 root). " +
        "Pass it in the mount options — the widget will not call any default URL. " +
        "Entering the unavailable state."
    );
  }
  // `headers` lets the integrator attach auth the mapper-api endpoints require
  // (`['public','private']` scope → x-api-key / x-client-id). Applied to every request.
  const http = axios.create({ baseURL: baseOpts.apiBaseUrl, headers: baseOpts.headers || {} });
  // Unique-per-instance id so the title clip-path (item 5) doesn't collide when multiple
  // previews are mounted on one page.
  const vpId = "vp" + Math.random().toString(36).slice(2, 9);

  // ---- per-instance persistent state ---------------------------------------
  // Field values persist across setFields() calls within a run. distance is server-set.
  const fields = {
    title: undefined,
    shipName: undefined,
    startDate: undefined,
    endDate: undefined,
    duration: undefined,
  };
  let distance; // server-driven mileage string (from the sync response)

  // FINAL-render mode (CONTRACT A / renderFinal): when true, composeFrame draws NO skeleton
  // placeholder rects — an unset field renders as nothing rather than a grey bar. Set once by
  // renderFinal (the final host page is single-use + screenshotted, then discarded) and never
  // by the live path, so the on-screen editing experience keeps its skeletons unchanged. All
  // TEXT positioning/layout rules (incl. the swap && titleEmpty fallbacks) are untouched.
  let finalMode = false;

  // ---- field-text streaming (build-phase, left→right per field) -------------
  // During a fresh build the ship-name + 3 detail rows "write in" sequentially via a left→right
  // clip wipe (the title is EXCLUDED — it binds live to user input). Purely presentational: the
  // values are already known when the build reaches its write window — host-supplied for ship/
  // dates/duration (run({fields}) / setFields), SERVER-supplied for mileage (sync `distance`).
  // streamActive gates it: true ONLY during renderInset's build, false for cached/instant
  // restores, Part A, and after the morph reveal — so known values render full text everywhere
  // except the live build. fieldStream holds each field's write progress 0..1: <=0 shows the
  // skeleton, 0<p<1 the partial wipe, >=1 the full text. fieldProgress() collapses to 1 (full)
  // whenever streaming is inactive, so the streaming logic never affects the non-build paths.
  const STREAM_FIELDS = ["shipName", "dates", "duration", "mileage"];
  let streamActive = false;
  const fieldStream = { shipName: 0, dates: 0, duration: 0, mileage: 0 };
  let streamRaf = 0;
  const fieldProgress = (key) => (streamActive ? (fieldStream[key] ?? 0) : 1);

  // The current run context — the same identifiers Shopify already holds (public_api_v3.md §3):
  // the voyage 5-tuple identity + the `_style` slug + print height/width + the customer's chosen
  // orientation. `layout` is an INPUT, not a toggle: a different orientation is a fresh run() (the
  // prototype's dual-orientation client cache + live-flip machinery is removed — brief §4 / D2).
  let current = {
    identity: null, // { ship_name, start_port, end_port, start_date, end_date }
    style: baseOpts.style,
    height: baseOpts.height,
    width: baseOpts.width,
    layout: baseOpts.layout,
  };

  // ---- status-event channel (D15 / public_api_v3.md §3) --------------------
  // The widget draws NO controls; it emits status events and Shopify attaches its own UI.
  //   ready         ({ resolved, unresolved })  — recipes preloaded; build/grey the size selector
  //   renderstart   ()                           — a run() began (Shopify spinner on)
  //   rendercomplete(result)                     — the map revealed (re-enable CTA)
  //   rendererror   (result)                     — render failed / no voyage / frames unavailable
  //   framefailed   ({ variant })                — setFrame asked for an unresolved variant
  const EVENTS = ["ready", "renderstart", "rendercomplete", "rendererror", "framefailed"];
  const listeners = Object.fromEntries(EVENTS.map((e) => [e, []]));
  function on(event, cb) {
    if (!listeners[event]) throw new Error(`createVoyagePreview: unknown event "${event}"`);
    if (typeof cb === "function") listeners[event].push(cb);
    return () => {
      const arr = listeners[event];
      const i = arr.indexOf(cb);
      if (i >= 0) arr.splice(i, 1);
    };
  }
  function emit(event, payload) {
    for (const cb of listeners[event] ? listeners[event].slice() : []) {
      try { cb(payload); } catch { /* a listener throwing must not break the widget */ }
    }
  }

  // ---- preloaded frame recipes (D15 / Pattern-1) ---------------------------
  // Fetched ONCE on mount from GET /api/v3/prints/frames, keyed by the RAW variant string, so
  // setFrame(variant) is a pure in-memory lookup. `activeVariant` is the single source of truth
  // for size/style/layout — run() renders whatever's active and never takes its own size.
  const frameRecipes = new Map(); // raw variant string -> frame object (§2, incl. height/width)
  let recipesLoaded = false;
  let resolvedStyle = baseOpts.style; // the NORMALISED style the endpoint echoes back
  let unresolvedVariants = [];
  let activeVariant = null;
  let pendingFrameVariant = null; // a setFrame() requested before recipes landed (applied on load)
  let framesRetries = 0;
  let framesRetryTimer = 0;
  let lastIdentity = null; // run(details) remembers these; bare run() re-renders them
  // Gate for a run() that arrives while a setFrame() is still pending recipe load. Without it the
  // run would paint AND POST the baked-in default (40×30 Portrait) and then get superseded by the
  // pending-frame replay — the portrait→landscape flash. Resolved when loadRecipes SETTLES
  // (success, terminal failure, or the no-apiBaseUrl mount) — never on a retry. The pending frame
  // is applied before resolution, so a parked run resumes with the real geometry.
  let settleRecipesGate;
  const recipesGate = new Promise((r) => (settleRecipesGate = r));
  let runCallToken = 0; // latest-wins across run() CALLS (runToken below guards renderInset internals)
  let destroyed = false;
  // The in-flight renderInset's `settle` resolver. Whoever supersedes that run — supersedeRun() or a
  // successor renderInset taking the slot — resolves it with a superseded result, so a superseded
  // run can never leave its caller awaiting forever. Settling an already-settled promise is a no-op,
  // so a slot left pointing at a completed run is harmless.
  let settleActiveRun = null;
  // Fresh object per settlement (never a shared singleton — hosts may mutate results). run() emits
  // NO event for it: the superseding call owns all events.
  const supersededResult = () => ({ outcome: "superseded" });

  // The currently mounted frame layout (so live-binding setFields can re-render
  // text on the existing frame without a fresh server call).
  let activeFrame = null; // the `frame` object currently composed
  let activeInsetDims = null; // insetDimensions of the mounted inset

  // DOM handles for the mounted product.
  let vpRoot = null; // full-size size-container that centres the shell
  let shell = null; // aspect-ratio box
  let frameSvg = null; // the print-space frame svg
  // coBranding OVERLAY, mounted ABOVE the inset so logos/ship inlays paint ON TOP of the map —
  // matching the print generator, which draws the map canvas and then #addBranding over it
  // (mapper-ui imageGenerator.ts:226 then :240, D10). It has to be a separate layer because
  // frameSvg carries the opaque page background: putting the whole frame above the inset would
  // hide the map. Styles whose logos sit in the bottom margin look identical either way; the
  // ones that place a logo over the map (Hurtigruten's second mark, HAL's ship inlay) only
  // render correctly with this layer.
  let brandingSvg = null;
  let insetBox = null; // the inset div
  let insetSvg = null; // inset-space animation svg
  let insetImg = null; // the morphing PNG
  let checklistHandle = null; // the floating anticipation checklist

  // animation handles (per active run)
  let raf = 0;
  let pollTimer = 0;
  let pulseAnim = null;
  let placeholderShimmerAnims = [];
  let cancelled = false; // supersede in-flight run on a new run / flip / clear
  let runToken = 0; // guards async callbacks against stale runs

  // ---- partner styling (D14) ----------------------------------------------
  // Kick off brand-font loading once at mount (Praxis/Termina/Inter via @font-face on
  // the API asset route). composeFrame wraps families with a system fallback, so text
  // paints immediately and re-renders crisp once the faces land (see the ready hook).
  const brandFontsReady = ensureBrandFonts(baseOpts.apiBaseUrl);
  // Natural pixel dims of preloaded coBranding assets (file -> {w,h}). The AppFactory
  // sizing math needs the image's natural w/h; we read it once per asset and cache it.
  const assetNatural = new Map();
  // Per-asset load promises (file -> Promise), so a caller that must not paint early can await
  // them — renderFinal does, because the composite driver screenshots the instant it resolves.
  // Without this the screenshot races the logo downloads and heavier brand assets (ship inlays
  // are 250KB-650KB) drop out silently, then get cached as a finished product. Resolves — never
  // rejects — on both success and failure: a missing logo leaves the slot empty in the live path,
  // and it is the composite driver's post-compose guard that turns that into a failed job.
  const assetLoads = new Map();
  // Ship coBranding lifecycle (plan §5/§6): false = show the grey Arvia skeleton
  // silhouette in the ship slot (load + trace); true = composite the style's real ship
  // (e.g. Arcadia) — flipped at the morph reveal (Part B).
  let shipRevealed = false;

  const styleAssetUrl = (file) =>
    `${baseOpts.apiBaseUrl || ""}${STYLE_ASSET_PATH}/${encodeURIComponent(file)}`;

  // Preload a coBranding asset, recording its natural w/h, then recompose so the logo
  // (now sized) appears. Cached: an asset's natural size never changes. Warms the image
  // so it's ready before the reveal (plan §7 preload). Failures are swallowed (the slot
  // just stays empty / skeleton).
  function preloadAsset(file) {
    if (assetNatural.has(file)) return assetLoads.get(file) || Promise.resolve();
    assetNatural.set(file, null); // in-flight marker (avoid duplicate loads)
    let settle;
    const loaded = new Promise((r) => { settle = r; });
    assetLoads.set(file, loaded);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Record natural size + the asset's VISIBLE (alpha) bounds as fractions 0..1 of the
      // natural box. Ship/logo PNGs carry transparent padding (e.g. arcadia_profile has ~37%
      // empty above the hull, ~28% to the right); skeletons and the title/ship clamps use the
      // visible bounds so a grey box matches the real ship's footprint and a title bar reserves
      // no space for a logo's empty margin. Same-origin asset → getImageData won't taint.
      const vis = computeVisibleBounds(img);
      // Bake the decoded bitmap to a data: URL and paint THAT (see drawCoBranding). Painting the
      // asset URL instead would make the SVG <image> issue a SECOND fetch — and in a different
      // CORS mode from this crossOrigin="anonymous" preload, so the preload never warms what the
      // SVG needs. That second load is unawaited, so the logo appears whenever it happens to
      // arrive: fine for the live preview (it recomposes), fatal for the composite, which
      // screenshots immediately and caches whatever painted in time. A data: URL removes the
      // fetch entirely, making both paths deterministic.
      assetNatural.set(file, { w: img.naturalWidth, h: img.naturalHeight, vis, href: toDataUrl(img) });
      if (!cancelled && activeFrame) composeFrame();
      settle(); // AFTER the recompose, so an awaiting screenshot sees the drawn logo
    };
    img.onerror = () => { assetNatural.delete(file); assetLoads.delete(file); settle(); };
    img.src = styleAssetUrl(file);
    return loaded;
  }

  // Re-encode a loaded image as a data: URL so the SVG can paint it with no second network
  // request. Returns null if the canvas read is blocked (cross-origin taint) — drawCoBranding
  // then falls back to the asset URL, i.e. exactly the old behaviour.
  function toDataUrl(img) {
    try {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      return c.toDataURL("image/png");
    } catch {
      return null;
    }
  }

  // Visible (alpha > threshold) bounding box of an image, as fractions {l,t,r,b} of its natural
  // box. Full-bleed / opaque images return {0,0,1,1}. Falls back to full bounds if the canvas
  // read is blocked (cross-origin taint) or the image is empty.
  function computeVisibleBounds(img) {
    const FULL = { l: 0, t: 0, r: 1, b: 1 };
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return FULL;
    try {
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, w, h).data;
      let minX = w, minY = h, maxX = -1, maxY = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (data[(y * w + x) * 4 + 3] > 8) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < minX) return FULL; // fully transparent
      return { l: minX / w, t: minY / h, r: (maxX + 1) / w, b: (maxY + 1) / h };
    } catch {
      return FULL;
    }
  }

  // Preload every coBranding asset for a frame's style so logos size in and the real ship
  // is warm before the morph reveal. (The pre-reveal ship slot is a plain grey box now, so
  // there's no skeleton asset to preload.)
  function preloadStyleAssets(coBranding) {
    return Promise.all((coBranding || []).map((c) => preloadAsset(c.file)));
  }

  // ===========================================================================
  // SHELL + FRAME COMPOSITION (client-side, from `frame` — C1/C2)
  // ===========================================================================

  function ensureShell() {
    if (shell) return shell;
    if (getComputedStyle(containerEl).position === "static") {
      containerEl.style.position = "relative";
    }
    // ROOT — fills the container with a DEFINITE width AND height and acts as a
    // size container so the shell's cqw/cqh units resolve (C3). It also centres
    // the shell so the fit-to-contain box sits in the middle on either axis.
    vpRoot = document.createElement("div");
    vpRoot.className = "vp-root";
    Object.assign(vpRoot.style, {
      position: "absolute",
      inset: "0",
      containerType: "size",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    });
    shell = document.createElement("div");
    shell.className = "vp-shell";
    Object.assign(shell.style, {
      position: "relative",
      // fit-to-contain on whichever axis binds (C3): height often binds for a
      // portrait product in a squarish slot. `width` is the smaller of the full
      // container width and the width implied by the full container height for
      // this aspect ratio; `aspect-ratio` derives the height. The actual
      // aspect-ratio + width expression is set per-render (printCanvas-driven).
      height: "auto",
      overflow: "hidden",
      lineHeight: "0",
    });
    vpRoot.appendChild(shell);
    containerEl.appendChild(vpRoot);
    return shell;
  }

  function clearAnimChrome() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = 0;
    if (pulseAnim) {
      try { pulseAnim.cancel(); } catch {}
      pulseAnim = null;
    }
  }

  function clearShimmer() {
    for (const a of placeholderShimmerAnims) {
      try { a.cancel(); } catch {}
    }
    placeholderShimmerAnims = [];
  }

  // ---- floating anticipation checklist (mounted on the SHELL, so it can bleed
  // past the inset edge onto the frame border) ------------------------------
  function teardownChecklist() {
    if (checklistHandle) {
      try { checklistHandle.destroy(); } catch {}
      checklistHandle = null;
    }
  }

  // Position the card at the inset's top-left, nudged out by checklistOffsetX/Y so
  // it overlaps the border and reads as hovering. Before a frame is mounted (cold
  // first load) it falls back to a sensible top-left of the shell.
  function positionChecklist(cfg) {
    if (!checklistHandle) return;
    const el = checklistHandle.el;
    if (activeFrame) {
      const { inset, printCanvas } = activeFrame;
      el.style.left = `${(inset.x / printCanvas.width) * 100}%`;
      el.style.top = `${(inset.y / printCanvas.height) * 100}%`;
    } else {
      el.style.left = "8%";
      el.style.top = "7%";
    }
    el.style.marginLeft = `${cfg.checklistOffsetX}px`;
    el.style.marginTop = `${cfg.checklistOffsetY}px`;
  }

  // ---- frame layout helpers (mirror imageGenerator.ts EXACTLY) -------------
  // Everything below reads frame.layout / frame.margins / frame.printCanvas; no
  // numbers are invented here. Coordinates are PRINT pixels.
  //
  // ⚠️ CROSS-REFERENCE (D10) — TWO frame-layout implementations, kept deliberately separate:
  //   1. THIS widget (customer-facing live preview), and
  //   2. the STAFF print generator: mapper-ui/src/app/components/MapImagePreview/js/imageGenerator.ts
  // Both read the SAME merged style-config keys to position the title / ship name / journey details
  // / divider / coBranding, so the on-screen preview matches the printed product. There is no
  // automated pixel-parity test between them (a cross-repo CI test is disproportionate). Instead:
  // THESE TWO MUST STAY VISUALLY IN STEP — if you change layout maths here, check imageGenerator.ts
  // (and vice-versa), or the preview and the print will drift.

  // Y-offset selector — mirrors imageGenerator #getYOffset.
  function getYOffset(frame, configKey) {
    const { printCanvas, margins, orientation, layout } = frame;
    const suffix = orientation === "Portrait" ? "PortraitAdjustment" : "LandscapeAdjustment";
    const adj = layout[`${configKey}${suffix}`];
    return printCanvas.height - margins.bottom + adj * printCanvas.height;
  }

  function makeSvgText(content, x, y, align, fontWeight, fontSize, fontFamily, color) {
    const t = document.createElementNS(SVG_NS, "text");
    t.setAttribute("x", String(x));
    t.setAttribute("y", String(y));
    // imageGenerator uses textBaseline = "top"; SVG equivalent:
    t.setAttribute("dominant-baseline", "text-before-edge");
    t.setAttribute("text-anchor", align === "right" ? "end" : align === "center" ? "middle" : "start");
    t.setAttribute("fill", color);
    t.setAttribute("font-family", fontFamilyWithFallback(fontFamily));
    t.setAttribute("font-weight", String(fontWeight));
    t.setAttribute("font-size", fontSize); // print-px string e.g. "70px"
    t.textContent = content;
    return t;
  }

  function makePlaceholderRect(x, y, w, h, align) {
    // Light-grey rounded rect sized to mimic the eventual text (C6).
    const r = document.createElementNS(SVG_NS, "rect");
    const rx = baseOpts.placeholderColor ? baseOpts.placeholderRadiusPx : 0;
    // x is the text anchor; for right-align the bar extends leftwards.
    const rectX = align === "right" ? x - w : x;
    r.setAttribute("x", String(rectX));
    r.setAttribute("y", String(y));
    r.setAttribute("width", String(w));
    r.setAttribute("height", String(h));
    r.setAttribute("rx", String(rx));
    r.setAttribute("ry", String(rx));
    r.setAttribute("fill", baseOpts.placeholderColor);
    if (baseOpts.placeholderShimmer) {
      const anim = r.animate(
        [{ opacity: 1 }, { opacity: 0.45 }, { opacity: 1 }],
        { duration: 1600, iterations: Infinity, easing: "ease-in-out" }
      );
      placeholderShimmerAnims.push(anim);
    }
    return r;
  }

  // pixel size of a font-size string ("70px" → 70).
  function fontPx(sizeStr) {
    const n = parseFloat(sizeStr);
    return Number.isNaN(n) ? 0 : n;
  }

  // Vertically centre a skeleton bar on the field's TEXT band so the placeholder lines up with
  // where the real field actually paints. The text is drawn with dominant-baseline:text-before-edge
  // (the em-box TOP sits at the field's offset Y), so its visible glyphs sit ~centred in the em box;
  // a top-anchored bar floats ~0.1–0.2em ABOVE the rendered text (most visible on the smaller cards
  // like A4, where the bottom band is tight). Centring on the em box makes skeleton↔text register at
  // every size. Fully config-driven: it uses the field's own resolved font size + its bar height.
  function skeletonBarY(textTopY, fontSizePx, barHeight) {
    return textTopY + fontSizePx / 2 - barHeight / 2;
  }

  /**
   * Visible print-space x-extents of the current frame's coBranding logos, using the SAME
   * AppFactory sizing math as drawCoBranding but trimmed to each asset's alpha bounds. Used to
   * stop title/ship skeletons (and the real title) short of a logo. Assets whose size/bounds
   * haven't loaded yet are skipped — composeFrame re-runs when they land (preloadAsset), so the
   * clamp tightens automatically. Returns [{left, right, top, bottom, role, file}] in print px.
   */
  function coBrandingExtents() {
    if (!activeFrame) return [];
    const { printCanvas, layout, isPortrait } = activeFrame;
    const out = [];
    for (const logo of layout.coBranding || []) {
      const nat = assetNatural.get(logo.file);
      if (!nat) continue;
      const { x, y, w, h } = coBrandingPrintBox(logo, nat, isPortrait, printCanvas);
      const vis = nat.vis || { l: 0, t: 0, r: 1, b: 1 };
      out.push({
        left: x + vis.l * w,   // visible (alpha) left edge
        right: x + vis.r * w,  // visible (alpha) right edge
        top: y + vis.t * h,    // visible (alpha) top edge
        bottom: y + vis.b * h, // visible (alpha) bottom edge
        role: logo.role,
        file: logo.file,
      });
    }
    return out;
  }

  // Is a streamed field's value currently known? Mirrors the render conditions in composeFrame:
  // ship/duration on `undefined`, dates on a producible range, mileage on the server `distance`.
  function fieldStreamHasValue(key) {
    switch (key) {
      case "shipName": return fields.shipName !== undefined;
      case "dates": return !!produceDisplayDateRange(fields.startDate, fields.endDate);
      case "duration": return fields.duration !== undefined;
      case "mileage": return distance !== undefined;
      default: return false;
    }
  }

  // Append a single-line field as TEXT, applying the left→right write wipe when mid-stream.
  // p>=1 → plain text; 0<p<1 → text under a clipPath rect that grows from the text's left edge
  // to its right (so it reads as being written L→R regardless of anchor). p<=0 is never passed
  // here (the caller draws a skeleton instead). The clip width uses the measured text width in
  // the field's own font, so it tracks the real glyphs (brand fonts re-measured on recompose).
  function appendStreamedText(content, x, y, align, weight, size, font, color, key) {
    const p = fieldProgress(key);
    const node = makeSvgText(content, x, y, align, weight, size, font, color);
    if (p >= 1) { frameSvg.appendChild(node); return; }
    const fontShorthand = `${weight} ${size} ${fontFamilyWithFallback(font)}`;
    const wText = measureTextWidth(content, fontShorthand);
    const leftEdge = align === "right" ? x - wText : x; // fields are only left/right-anchored
    const fontH = fontPx(size);
    const clipId = `${vpId}-stream-${key}`;
    const clip = document.createElementNS(SVG_NS, "clipPath");
    clip.setAttribute("id", clipId);
    const cr = document.createElementNS(SVG_NS, "rect");
    cr.setAttribute("x", String(leftEdge));
    cr.setAttribute("y", String(y - fontH * 0.3));
    cr.setAttribute("width", String(Math.max(0, wText * p)));
    cr.setAttribute("height", String(fontH * 1.6));
    clip.appendChild(cr);
    frameSvg.appendChild(clip);
    const g = document.createElementNS(SVG_NS, "g");
    g.setAttribute("clip-path", `url(#${clipId})`);
    g.appendChild(node);
    frameSvg.appendChild(g);
  }

  function cancelFieldStream() { if (streamRaf) { cancelAnimationFrame(streamRaf); streamRaf = 0; } }

  const prefersReducedMotion = () =>
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Drive the sequential write: ship name → dates → duration → mileage. Each present field
  // ramps 0→1 over streamFieldMs, then a streamGapMs beat, then the next; absent fields are
  // skipped instantly (they stay skeleton until their value lands, then snap full at the reveal).
  // Reduced motion → snap all known fields to full, no wipe. Guarded by the run token.
  function startFieldStream(token) {
    cancelFieldStream();
    if (prefersReducedMotion()) {
      for (const k of STREAM_FIELDS) if (fieldStreamHasValue(k)) fieldStream[k] = 1;
      if (!cancelled && token === runToken) composeFrame();
      return;
    }
    let idx = 0;
    let mode = "write"; // "write" | "gap"
    let markStart = 0;
    const tick = (now) => {
      streamRaf = 0;
      if (cancelled || token !== runToken || !streamActive) return;
      if (idx >= STREAM_FIELDS.length) return; // sequence complete
      const key = STREAM_FIELDS[idx];
      if (!fieldStreamHasValue(key)) { idx++; markStart = 0; mode = "write"; streamRaf = requestAnimationFrame(tick); return; }
      if (!markStart) markStart = now;
      if (mode === "write") {
        const p = Math.min(1, (now - markStart) / baseOpts.streamFieldMs);
        fieldStream[key] = p;
        composeFrame();
        if (p >= 1) { mode = "gap"; markStart = now; }
      } else if (now - markStart >= baseOpts.streamGapMs) {
        idx++; markStart = 0; mode = "write";
      }
      streamRaf = requestAnimationFrame(tick);
    };
    streamRaf = requestAnimationFrame(tick);
  }

  // End the stream: every known field resolves to full text, streaming gate drops (so any later
  // setFields / cached restore shows full text directly). Called at the morph reveal.
  function finishFieldStream() {
    cancelFieldStream();
    for (const k of STREAM_FIELDS) fieldStream[k] = 1;
    streamActive = false;
  }

  // Latest-wins teardown: cancel any in-flight animated run and stop its chrome. This is the shared
  // supersede sequence the public methods (setFrame / renderFinal / clear / destroy) run before they
  // take over. Flags carve out the genuine per-site differences (they are NOT all identical):
  //   • resetFieldStream (default true) — cancel + reset the build-phase field stream. destroy() sets
  //     this false: it never touched the field stream (its cancelled/runToken bump already neutralises
  //     any pending tick), and it is about to null the DOM out entirely.
  //   • resetFacts — clear the four SERVER-driven facts (shipName/startDate/endDate/duration) and the
  //     map-derived distance back to skeleton. The title is NOT touched here (it is host-editable);
  //     callers that also clear the title do so locally. setFrame does its facts reset locally instead,
  //     because it must land AFTER its recipe-lookup guards (so guard-return paths keep their values).
  //   • abandonParkedRun (default true) — also abandon a run() parked on the recipes gate. setFrame
  //     sets this false: a parked run must SURVIVE frame changes (incl. the pending-frame replay on
  //     recipe load) and render the newest active frame, not die to the very setFrame it waits for.
  function supersedeRun({ resetFacts = false, resetFieldStream = true, abandonParkedRun = true } = {}) {
    cancelled = true;
    runToken++;
    if (abandonParkedRun) runCallToken++;
    // Settle the superseded run's promise so its caller never awaits forever (no event fires for
    // a superseded result — this caller owns the events from here).
    if (settleActiveRun) { settleActiveRun(supersededResult()); settleActiveRun = null; }
    clearAnimChrome();
    clearShimmer();
    teardownChecklist();
    if (resetFieldStream) {
      cancelFieldStream();
      streamActive = false;
      for (const k of STREAM_FIELDS) fieldStream[k] = 0;
    }
    if (resetFacts) {
      fields.shipName = undefined;
      fields.startDate = undefined;
      fields.endDate = undefined;
      fields.duration = undefined;
      distance = undefined;   // mileage is map-derived — cleared until a run() re-renders it
    }
  }

  /**
   * (Re)compose the FRAME svg from activeFrame + the current field values.
   * Called on mount and on every setFields() (live-binding, C6). Frame stays
   * structurally constant; only text/placeholder nodes change.
   */
  function composeFrame() {
    if (!activeFrame || !frameSvg) return;
    clearShimmer();
    frameSvg.replaceChildren();
    if (brandingSvg) brandingSvg.replaceChildren();

    const { printCanvas, margins, layout } = activeFrame;
    const W = printCanvas.width;
    const H = printCanvas.height;
    const L = margins.left;
    const R = margins.right;
    const colRight = W - R; // right-aligned text/detail anchor x
    const colWidth = W - L - R; // available text column width

    // -- page background (the white page; #drawWhiteBorder) ------------------
    const bg = document.createElementNS(SVG_NS, "rect");
    bg.setAttribute("x", "0");
    bg.setAttribute("y", "0");
    bg.setAttribute("width", String(W));
    bg.setAttribute("height", String(H));
    bg.setAttribute("fill", layout.pageBackgroundColor);
    frameSvg.appendChild(bg);

    // -- divider line (#drawMapElements / #drawLine) -------------------------
    const lineY = H - margins.bottom + layout.imageLineYoffOffsetAdjustment * H;
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(L));
    line.setAttribute("y1", String(lineY));
    line.setAttribute("x2", String(colRight));
    line.setAttribute("y2", String(lineY));
    line.setAttribute("stroke", layout.bottomLineColor);
    line.setAttribute("stroke-width", String(layout.imageLineWidth));
    frameSvg.appendChild(line);

    const swap = layout.swapTitleAndShipName;
    const titleValue = (fields.title ?? "").toString();
    const titleEmpty = titleValue.trim() === "";

    // Visible logo extents + the min gap a bar keeps from a logo (both print px). Used to clamp
    // the title and (swapped) ship-name bars off the centred/partner logos — responsive to config.
    const logoExtents = coBrandingExtents();
    const logoPad = W * baseOpts.placeholderLogoGapFracPage;

    // -- JOURNEY DETAILS pre-compute: dates → duration → mileage text -------
    // Mirrors #drawJourneyDates/#drawJourneyDuration/#drawJourneyMileage incl.
    // the `swap && titleEmpty` fallbacks (kept explicit in each resolveY below).
    const datesText = produceDisplayDateRange(fields.startDate, fields.endDate);
    const durationText = pluraliseDays(fields.duration);
    const mileageText = (distance ?? "").toString();

    // The dates/duration Y positions are needed by the swap/titleEmpty cascade of the
    // fields BELOW them (duration falls back off dates; mileage falls back off duration),
    // so they are hoisted here and assigned inside each descriptor's resolveY().
    let datesY;
    let durationY;

    // Title geometry (shared by the title descriptor's skeleton + value paths). swap flips the
    // title to the right column; the SAME clamped width drives both the skeleton bar and the
    // 2-line wrap so they share one footprint (clampWidthToLogos keeps both clear of the logos).
    // Only logos on the title's first row clamp it — a logo sitting under the title (Emma's
    // limited-edition mark) used to collapse the width to the floor and wrap every word.
    const titleX = swap ? colRight : L;
    const titleY = H - margins.bottom + layout.imageMapTitleYOffsetAdjustment * H;
    const titleAlign = swap ? "right" : "left";
    const titleBand = { top: titleY, bottom: titleY + fontPx(layout.imageTitleFontSize) };
    const titleMaxWidth = clampWidthToLogos(
      titleX, titleAlign, colWidth * baseOpts.placeholderTitleWidthFrac, logoExtents, logoPad, titleBand
    );

    // ONE guarded draw for every field. resolveY() returns the field's baseline Y (and may
    // record datesY/durationY for the cascade); isUnknown(y) decides skeleton-vs-value;
    // drawSkeleton(y) paints the placeholder bar; drawValue(y) paints the real field. The
    // finalMode → no-skeleton rule (composite/final path) is enforced HERE, once, for all five.
    function drawField(desc) {
      if (!desc.display) return;
      const y = desc.resolveY();
      if (desc.isUnknown(y)) {
        if (!finalMode) desc.drawSkeleton(y); // single-point final-mode skeleton guard
      } else {
        desc.drawValue(y);
      }
    }

    // Field-descriptor table (D10 twin of mapper-ui imageGenerator.ts — the per-field CONFIG KEY
    // names are kept as literals here so the two files stay visually cross-checkable). One entry
    // per field, in draw order: title, shipName, dates, duration, mileage.
    const fieldDescriptors = [
      // -- TITLE (#drawMapTitle) — genuinely distinct: 2-line clip-wrap render (makeSvgText,
      // not appendStreamedText) and a logo-clamped skeleton width. Kept in the table so the
      // single finalMode guard covers it too, with its own resolve/skeleton/value closures.
      {
        display: layout.imageTitleDisplayed,
        // FEEDBACK item 5: the title spans a fraction of the text column (placeholderTitleWidthFrac);
        // once it would exceed that it wraps to a 2nd row, and anything past two rows (or a single
        // word wider than the cap) is visually CLIPPED — never spilling further. The clamped width
        // (titleMaxWidth above) drives both the skeleton bar and the wrap so they share one footprint.
        resolveY() { return titleY; },
        isUnknown() { return fields.title === undefined; },
        drawSkeleton(titleY) {
          const h = fontPx(layout.imageTitleFontSize) * baseOpts.placeholderHeightFrac;
          const yBar = skeletonBarY(titleY, fontPx(layout.imageTitleFontSize), h);
          frameSvg.appendChild(makePlaceholderRect(titleX, yBar, titleMaxWidth, h, titleAlign));
        },
        drawValue(titleY) {
          const fontShorthand = `${layout.imageTitleFontWeight} ${layout.imageTitleFontSize} ${fontFamilyWithFallback(layout.imageTitleFont)}`;
          const lineSpacing = layout.imageTitleSplitLineSpacing;
          const fontH = fontPx(layout.imageTitleFontSize);
          const lines = wrapTitleLines(titleValue, titleMaxWidth, fontShorthand).slice(0, 2);

          // clip box: 50%-wide × two rows, anchored to the title side (left, or right when swapped).
          const clipId = `${vpId}-title`;
          const clip = document.createElementNS(SVG_NS, "clipPath");
          clip.setAttribute("id", clipId);
          const clipRect = document.createElementNS(SVG_NS, "rect");
          clipRect.setAttribute("x", String(titleAlign === "right" ? titleX - titleMaxWidth : titleX));
          clipRect.setAttribute("y", String(titleY - fontH * 0.25));
          clipRect.setAttribute("width", String(titleMaxWidth));
          clipRect.setAttribute("height", String(lineSpacing + fontH * 1.4));
          clip.appendChild(clipRect);
          frameSvg.appendChild(clip);

          const g = document.createElementNS(SVG_NS, "g");
          g.setAttribute("clip-path", `url(#${clipId})`);
          lines.forEach((ln, i) => {
            g.appendChild(makeSvgText(
              ln, titleX, titleY + i * lineSpacing, titleAlign,
              layout.imageTitleFontWeight, layout.imageTitleFontSize,
              layout.imageTitleFont, layout.imageTitleFontColor
            ));
          });
          frameSvg.appendChild(g);
        },
      },
      // -- SHIP NAME (#drawShipName) — item 7: biggest/widest of the right stack (~15% of page).
      {
        display: layout.imageShipNameDisplayed,
        resolveY() { return H - margins.bottom + layout.imageShipNameYOffsetAdjustment * H; },
        // skeleton: value not yet known, OR known but its build-write hasn't begun.
        isUnknown() { return fields.shipName === undefined || fieldProgress("shipName") <= 0; },
        drawSkeleton(shipY) {
          const shipX = swap ? L : colRight;
          const shipAlign = swap ? "left" : "right";
          const h = fontPx(layout.imageShipNameFontSize) * baseOpts.placeholderHeightFrac;
          const w = W * baseOpts.placeholderShipWidthFracPage;
          frameSvg.appendChild(makePlaceholderRect(shipX, skeletonBarY(shipY, fontPx(layout.imageShipNameFontSize), h), w, h, shipAlign));
        },
        drawValue(shipY) {
          const shipX = swap ? L : colRight;
          const shipAlign = swap ? "left" : "right";
          appendStreamedText(
            (fields.shipName ?? "").toString(), shipX, shipY, shipAlign,
            layout.imageShipNameFontWeight, layout.imageShipNameFontSize,
            layout.imageShipNameFont, layout.imageShipNameFontColor, "shipName"
          );
        },
      },
      // -- DATES (#drawJourneyDates) — right-aligned; swap&&titleEmpty ⇒ take the title slot.
      {
        display: layout.imageJourneyDatesDisplayed,
        resolveY() {
          datesY = (swap && titleEmpty)
            ? H - margins.bottom + layout.imageMapTitleYOffsetAdjustment * H
            : getYOffset(activeFrame, "imageEndDateYOffset");
          return datesY;
        },
        isUnknown() {
          const datesUnknown = fields.startDate === undefined && fields.endDate === undefined;
          return datesUnknown || (datesText && fieldProgress("dates") <= 0);
        },
        drawSkeleton(datesY) {
          const h = fontPx(layout.imageJourneyDatesFontSize) * baseOpts.placeholderDetailHeightFrac;
          const w = W * baseOpts.placeholderDatesWidthFracPage;
          frameSvg.appendChild(makePlaceholderRect(colRight, skeletonBarY(datesY, fontPx(layout.imageJourneyDatesFontSize), h), w, h, "right"));
        },
        drawValue(datesY) {
          if (!datesText) return; // matches the original `else if (datesText)` guard
          appendStreamedText(
            datesText, colRight, datesY, "right",
            layout.imageJourneyDatesFontWeight, layout.imageJourneyDatesFontSize,
            layout.imageJourneyDatesFont, layout.imageJourneyDatesFontColor, "dates"
          );
        },
      },
      // -- DURATION (#drawJourneyDuration) — right-aligned; swap&&titleEmpty ⇒ fall off dates.
      {
        display: layout.imageDurationDisplayed,
        resolveY() {
          if (swap && titleEmpty) {
            const portrait = activeFrame.orientation === "Portrait";
            const gap = portrait
              ? (layout.imageDurationYOffsetPortraitAdjustment - layout.imageEndDateYOffsetPortraitAdjustment) * H
              : (layout.imageDurationYOffsetLandscapeAdjustment - layout.imageEndDateYOffsetLandscapeAdjustment) * H;
            durationY = (datesY ?? getYOffset(activeFrame, "imageEndDateYOffset")) + gap;
          } else {
            durationY = getYOffset(activeFrame, "imageDurationYOffset");
          }
          return durationY;
        },
        isUnknown() { return fields.duration === undefined || (durationText && fieldProgress("duration") <= 0); },
        drawSkeleton(durationY) {
          const h = fontPx(layout.imageDurationFontSize) * baseOpts.placeholderDetailHeightFrac;
          const w = W * baseOpts.placeholderDurationWidthFracPage;
          frameSvg.appendChild(makePlaceholderRect(colRight, skeletonBarY(durationY, fontPx(layout.imageDurationFontSize), h), w, h, "right"));
        },
        drawValue(durationY) {
          if (!durationText) return; // matches the original `else if (durationText)` guard
          appendStreamedText(
            durationText, colRight, durationY, "right",
            layout.imageDurationFontWeight, layout.imageDurationFontSize,
            layout.imageDurationFont, layout.imageDurationFontColor, "duration"
          );
        },
      },
      // -- MILEAGE (#drawJourneyMileage) — right-aligned; swap&&titleEmpty ⇒ fall off duration.
      // Genuinely-absent distance renders as nothing; when the backend threads numNMiles through,
      // `distance` is set and the real mileage renders (see renderFinal).
      {
        display: layout.imageDistanceDisplayed,
        resolveY() {
          if (swap && titleEmpty) {
            const portrait = activeFrame.orientation === "Portrait";
            const gap = portrait
              ? (layout.imageMileageYOffsetPortraitAdjustment - layout.imageDurationYOffsetPortraitAdjustment) * H
              : (layout.imageMileageYOffsetLandscapeAdjustment - layout.imageDurationYOffsetLandscapeAdjustment) * H;
            return (durationY ?? getYOffset(activeFrame, "imageDurationYOffset")) + gap;
          }
          return getYOffset(activeFrame, "imageMileageYOffset");
        },
        isUnknown() { return distance === undefined || (mileageText && fieldProgress("mileage") <= 0); },
        drawSkeleton(mileageY) {
          const h = fontPx(layout.imageDistanceFontSize) * baseOpts.placeholderDetailHeightFrac;
          const w = W * baseOpts.placeholderMileageWidthFracPage;
          frameSvg.appendChild(makePlaceholderRect(colRight, skeletonBarY(mileageY, fontPx(layout.imageDistanceFontSize), h), w, h, "right"));
        },
        drawValue(mileageY) {
          if (!mileageText) return; // matches the original `else if (mileageText)` guard
          appendStreamedText(
            mileageText, colRight, mileageY, "right",
            layout.imageDistanceFontWeight, layout.imageDistanceFontSize,
            layout.imageDistanceFont, layout.imageDistanceFontColor, "mileage"
          );
        },
      },
    ];

    for (const desc of fieldDescriptors) drawField(desc);

    // -- coBRANDING (#addBranding) — logos + ship, role-aware (D14) ----------
    drawCoBranding();
  }

  /**
   * Composite the style's coBranding (logos + ship) into the frame SVG. Geometry comes from
   * branding.js (print-absolute size, centred on xPos/yPos — exactly as the print generator draws it).
   *
   * Roles (plan §5): `logo` paints the real image immediately; `ship` paints the grey
   * Arvia skeleton silhouette until `shipRevealed`, then the style's real ship.
   */
  function drawCoBranding() {
    const { printCanvas, layout, isPortrait } = activeFrame;
    const coBranding = layout.coBranding || [];
    if (!coBranding.length || !brandingSvg) return;

    for (const logo of coBranding) {
      const nat = assetNatural.get(logo.file);
      if (!nat) { preloadAsset(logo.file); continue; } // size in once natural w/h lands
      const { x: xPrint, y: yPrint, w: wPrint, h: hPrint } = coBrandingPrintBox(logo, nat, isPortrait, printCanvas);

      const isShip = logo.role === "ship";
      if (isShip && !shipRevealed) {
        // Ship slot, pre-reveal: a GENERIC grey BOX — NOT a silhouette (a real one read too close
        // to the final ship). Sized to the ship's VISIBLE (alpha) bounds, not the full PNG box:
        // ship profiles carry large transparent margins (arcadia ~37% above the hull, ~28% right)
        // and filling them made the box read taller/wider than the real ship and crowd the
        // (swapped) ship-name field above it. The morph reveal swaps in the real ship (Part B).
        const vis = nat.vis || { l: 0, t: 0, r: 1, b: 1 };
        brandingSvg.appendChild(makePlaceholderRect(
          xPrint + vis.l * wPrint, yPrint + vis.t * hPrint,
          (vis.r - vis.l) * wPrint, (vis.b - vis.t) * hPrint, "left"
        ));
        continue;
      }
      // logo (always real) or the revealed ship (real image at/after the morph beat).
      const im = document.createElementNS(SVG_NS, "image");
      im.setAttribute("x", String(xPrint));
      im.setAttribute("y", String(yPrint));
      im.setAttribute("width", String(wPrint));
      im.setAttribute("height", String(hPrint));
      im.setAttribute("preserveAspectRatio", "xMidYMid meet");
      // Prefer the baked data: URL (no second fetch, paints synchronously); fall back to the
      // asset URL only if the canvas bake was blocked.
      const href = nat.href || styleAssetUrl(logo.file);
      im.setAttributeNS(XLINK_NS, "href", href);
      im.setAttribute("href", href); // modern UAs
      brandingSvg.appendChild(im);
    }
  }

  /**
   * Mount the shell + frame + (empty) inset box for the given frame layout.
   * Used both for the blank state and for each render/flip. Returns nothing;
   * the inset animation is wired separately by the run path.
   */
  function mountFrame(frame) {
    ensureShell();
    clearAnimChrome();
    clearShimmer();
    shell.replaceChildren();

    activeFrame = frame;
    const { printCanvas, inset } = frame;

    // aspect-ratio box from the print canvas (C3). Size it to fit-to-contain on
    // whichever axis binds, in pure CSS via container-query units resolved
    // against the full-size .vp-root size container:
    //   width  = min(100cqw, 100cqh * (W/H))   ← picks the binding axis
    //   height = auto                          ← derived from aspect-ratio
    // This recomputes automatically on resize — no JS re-render (C3).
    shell.style.aspectRatio = `${printCanvas.width} / ${printCanvas.height}`;
    shell.style.width = `min(100cqw, calc(100cqh * (${printCanvas.width} / ${printCanvas.height})))`;
    shell.style.height = "auto";

    // FRAME svg — print-pixel viewBox absolutely covering the shell.
    frameSvg = document.createElementNS(SVG_NS, "svg");
    frameSvg.setAttribute("viewBox", `0 0 ${printCanvas.width} ${printCanvas.height}`);
    frameSvg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    Object.assign(frameSvg.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%", display: "block",
    });
    shell.appendChild(frameSvg);

    // INSET box — positioned at frame.inset as % of printCanvas (C3/C4). %-based
    // so it scales with the viewBox; no JS recompute on resize.
    insetBox = document.createElement("div");
    insetBox.className = "vp-inset";
    Object.assign(insetBox.style, {
      position: "absolute",
      left: `${(inset.x / printCanvas.width) * 100}%`,
      top: `${(inset.y / printCanvas.height) * 100}%`,
      width: `${(inset.width / printCanvas.width) * 100}%`,
      height: `${(inset.height / printCanvas.height) * 100}%`,
      overflow: "hidden",
      background: baseOpts.insetBgColor,
      lineHeight: "0",
    });
    shell.appendChild(insetBox);

    // coBRANDING overlay — same print-space viewBox, appended AFTER the inset so it paints over
    // the map (see the brandingSvg declaration). Transparent and non-interactive, so it changes
    // nothing for styles whose logos sit in the margins.
    brandingSvg = document.createElementNS(SVG_NS, "svg");
    brandingSvg.setAttribute("viewBox", `0 0 ${printCanvas.width} ${printCanvas.height}`);
    brandingSvg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    Object.assign(brandingSvg.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%", display: "block",
      pointerEvents: "none",
    });
    shell.appendChild(brandingSvg);

    // reset inset-layer handles (rebuilt per render)
    insetSvg = null;
    insetImg = null;

    // Preload this style's coBranding (logos + ship skeleton) so they size in and are
    // warm before the reveal, then compose. Brand fonts re-compose once they land so the
    // frame text swaps from the system fallback to the real face (and title-wrap re-measures).
    preloadStyleAssets(frame.layout.coBranding);
    composeFrame();
    brandFontsReady.then(() => { if (!cancelled && activeFrame === frame) composeFrame(); });
  }

  // (Failure/unavailable overlays were removed 2026-07-03 — the widget no longer paints any failure
  // copy on screen. On failure it freezes in place and reports via the event/return API only; see
  // failFrozen. The storefront owns all failure UX.)

  // ===========================================================================
  // INSET RENDER PATHS
  // ===========================================================================

  /**
   * ANIMATED render of the inset — the reused choreography, scoped to insetBox.
   * `data` is the sync response. Resolves the provided promise when settled. A
   * server-side cache hit still plays the choreography (the poll returns a ready
   * imageUrl immediately and the morph reveals it) — D2 single-source registration.
   */
  function animateInset(data, cfg, settle, token) {
    const { jobId, insetDimensions, pixelPath, portPoints, skeleton, imageUrl: syncImageUrl } = data;
    activeInsetDims = insetDimensions;
    const W = insetDimensions.width;
    const H = insetDimensions.height;

    // Label status (D7) — surfaced from the poll (public_api_v3.md §1b) and passed up through the
    // reveal outcome so the storefront can show a "too many ports to label here" message when the
    // map rendered label-less. Counts/reason only (D11-safe — no lat/lng). Stays null until a poll
    // (or a server-cached sync) supplies it.
    let labels = data.labels ?? null;

    // ANTIMERIDIAN SEAM (D13): the server renders the base map with
    // `renderWorldCopies:false` — ONE world (±180°), never repeated — and wraps
    // the route's `pixelPath` longitudes into ±180 so every point lands inside
    // that single world. For a transmeridian / world voyage this means the
    // `pixelPath` contains exactly ONE large discontinuity: a consecutive-point
    // "jump" where the route crosses the antimeridian (it exits one edge, and
    // the next point is at the opposite edge). We split the traced polyline at
    // that seam (see `splitRouteSubpaths`) into separate path elements so no
    // line streaks across the whole inset, the subpaths reveal sequentially,
    // and the single dot naturally jumps edge→edge. Non-wrapping routes have no
    // such jump → one continuous subpath, unchanged.

    const stale = () => cancelled || token !== runToken;

    // inset-space SVG overlay (route + skeleton).
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    Object.assign(svg.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%", display: "block",
    });
    insetSvg = svg;

    // -- skeleton coastline group (transparent until ~2/3) -------------------
    const skelGroup = document.createElementNS(SVG_NS, "g");
    Object.assign(skelGroup.style, {
      opacity: "0",
      transition: `opacity ${cfg.skeletonFadeMs}ms ease`,
    });
    const skelPaths = (skeleton && skeleton.paths) || [];
    for (const d of skelPaths) {
      const p = document.createElementNS(SVG_NS, "path");
      p.setAttribute("d", d);
      p.setAttribute("fill", "none");
      p.setAttribute("stroke", cfg.skeletonColor);
      p.setAttribute("stroke-width", String(cfg.skeletonWidth));
      p.setAttribute("stroke-opacity", String(cfg.skeletonOpacity));
      p.setAttribute("stroke-linejoin", "round");
      p.setAttribute("stroke-linecap", "round");
      skelGroup.appendChild(p);
    }
    svg.appendChild(skelGroup);

    // -- the route we trace (inset space), as SEQUENTIAL subpaths ------------
    // Split `pixelPath` into contiguous subpaths at the antimeridian seam (the
    // one large consecutive-point jump a wrapped transmeridian route contains).
    // Each subpath gets its OWN <path> element so the dash reveal is strictly
    // SEQUENTIAL: a single <path> with two M…L… subpaths would reveal BOTH
    // subpaths proportionally and simultaneously under one strokeDasharray,
    // because the dash pattern restarts per-subpath. With separate elements we
    // drive each one's dashoffset from a shared cumulative drawn length, so the
    // far-side segment only begins once the near side is fully drawn.
    // Non-transmeridian routes yield exactly ONE subpath → unchanged behaviour.
    const subpaths = splitRouteSubpaths(pixelPath, W / 2);
    const routePaths = []; // { el, len, start, points, manual } per subpath, in draw order
    let totalLen = 0;
    for (const pts of subpaths) {
      const p = document.createElementNS(SVG_NS, "path");
      p.setAttribute("d", subpathToD(pts));
      p.setAttribute("fill", "none");
      p.setAttribute("stroke", cfg.routeColor);
      p.setAttribute("stroke-width", String(cfg.routeWidth));
      p.setAttribute("stroke-linejoin", "round");
      p.setAttribute("stroke-linecap", "round");
      svg.appendChild(p);
      const len = p.getTotalLength();
      // hide initially: full dash, full offset.
      p.style.strokeDasharray = String(len);
      p.style.strokeDashoffset = String(len);
      // manual polyline length (Σ segment lengths) — used to map port arc-lengths into
      // this same dash-reveal space (FEEDBACK item 2).
      let manual = 0;
      for (let i = 0; i < pts.length - 1; i++) manual += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1]);
      routePaths.push({ el: p, len, start: totalLen, points: pts, manual });
      totalLen += len;
    }

    // -- PROGRESSIVE PORT MARKERS (FEEDBACK item 2) --------------------------
    // Project each port point onto the route to get its arc-length `s`, sort in travel
    // order, and create a hidden marker that pops the moment the trace head reaches `s`.
    // Markers match the rendered PNG's port circles so the morph is seamless. The marker
    // fractions also drive a fixed-duration speed dip (see the time-warp below).
    const markers = [];
    if (cfg.portMarkers && Array.isArray(portPoints) && totalLen > 0) {
      for (const pt of portPoints) {
        const { s } = arcLengthOfPoint(routePaths, pt);
        // Outer <g> carries the (static) position; the inner circle is what pops, so the
        // scale animation can't clobber the translate.
        const g = document.createElementNS(SVG_NS, "g");
        g.setAttribute("transform", `translate(${pt[0]} ${pt[1]})`);
        const c = document.createElementNS(SVG_NS, "circle");
        c.setAttribute("cx", "0");
        c.setAttribute("cy", "0");
        c.setAttribute("r", String(cfg.portMarkerRadius));
        c.setAttribute("fill", cfg.portMarkerColor);
        c.setAttribute("stroke", cfg.portMarkerStrokeColor);
        c.setAttribute("stroke-width", String(cfg.portMarkerStrokeWidth));
        Object.assign(c.style, { opacity: "0", transformBox: "fill-box", transformOrigin: "center" });
        g.appendChild(c);
        svg.appendChild(g);
        markers.push({ el: c, s, revealed: false });
      }
      markers.sort((a, b) => a.s - b.s);
    }

    // Fixed-duration time-warp that eases the trace as it nears each port (the A→B→C
    // beat) without changing total duration. Built from interior+end port fractions
    // (skip the start marker at s≈0). Falls back to the plain trace curve when disabled
    // or when there are no ports to ease at.
    const portFractions = markers
      .map((m) => m.s / totalLen)
      .filter((f) => f > 0.015);
    const portWarp =
      cfg.portEase && portFractions.length
        ? buildPortTimeWarp(portFractions, cfg.portEaseDepth, cfg.portEaseWidth, cfg.portEaseMinSpeed)
        : null;

    const head = document.createElementNS(SVG_NS, "circle");
    head.setAttribute("r", String(cfg.routeWidth + 2.5));
    head.setAttribute("fill", "#ffffff");
    head.setAttribute("stroke", cfg.accentColor);
    head.setAttribute("stroke-width", "2");
    svg.appendChild(head);

    // -- the PNG that morphs in (hidden until reveal) ------------------------
    const img = document.createElement("img");
    Object.assign(img.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%",
      objectFit: "fill", opacity: "0",
      transition: `opacity ${cfg.revealMs}ms ease`, pointerEvents: "none",
    });
    insetImg = img;

    insetBox.append(svg, img);

    // Stage status now lives in the floating checklist (created during the fetch
    // beat in renderInset and still mounted on the shell). The route trace begins
    // here, so advance it to stage 1 "Building your route".
    if (checklistHandle) checklistHandle.advanceTo(1);

    // ---- trace geometry ----------------------------------------------------
    // totalLen is the sum of all subpath lengths (computed above). Each subpath
    // is initialised hidden (dashoffset = its own length).
    const traceMs = clamp(cfg.traceMs, cfg.traceFloorMs, cfg.traceCeilMs);

    let traceDone = false;
    let imageReady = false;
    let imageUrl = null;
    let revealed = false;
    let skeletonShown = false;
    let traceEndAt = 0; // performance.now() when the trace finished (drives the map-ready hold)
    const t0 = performance.now();

    function doReveal() {
      revealed = true;
      // The build is over: resolve every known field to full text and drop the streaming gate so
      // any later setFields renders text directly. (Fields never supplied stay skeleton — they're
      // still genuinely unknown.)
      finishFieldStream();
      composeFrame();
      // Tick the final "Map ready" stage and retire the logo, then fade the card
      // away a beat later so the finished map gets the stage to itself.
      if (checklistHandle) {
        checklistHandle.complete();
        const ch = checklistHandle;
        setTimeout(() => { if (!stale()) ch.fadeOut(); }, 280);
      }
      const pngEl = img;
      pngEl.onload = () => {
        if (stale()) return;
        pngEl.style.opacity = "1";
        // Part B reveal (plan §5/§6): swap the grey Arvia ship skeleton for the style's
        // REAL ship (e.g. Arcadia) at the morph beat. composeFrame redraws coBranding with
        // shipRevealed=true. No-op for styles without a ship.
        if (!shipRevealed) { shipRevealed = true; composeFrame(); }
        setTimeout(() => {
          if (stale()) return;
          svg.style.transition = `opacity ${cfg.revealMs}ms ease`;
          svg.style.opacity = "0";
          teardownChecklist();
          settle({ outcome: "revealed", imageUrl, labels });
        }, cfg.revealMs);
      };
      pngEl.onerror = () => {
        if (!stale()) failFrozen(settle, { outcome: "terminal", reason: "render-failed" });
      };
      pngEl.src = imageUrl;
    }

    // Reveal only once: the trace has finished, the REAL png is ready, AND the
    // deliberate "Map ready" beat has been held (mapReadyHoldMs after the trace
    // ended). If the image lands later than that beat, reveal as soon as it does.
    function maybeReveal() {
      if (revealed || stale()) return;
      if (!imageReady || !traceDone) return;
      const now = performance.now();
      const wait = Math.max(
        cfg.mapReadyHoldMs - (now - traceEndAt), // hold the "Map ready" beat
        cfg.traceFloorMs - (now - t0)            // floor safety (subsumed by the staged arc)
      );
      if (wait > 0) {
        pollTimer = setTimeout(maybeReveal, wait);
        return;
      }
      doReveal();
    }

    function onTraceEnd() {
      head.style.transition = "opacity 300ms ease";
      head.style.opacity = "0";
      traceEndAt = performance.now();
      // Trace finished → "Map ready" becomes the active stage (it ticks at reveal,
      // once the real png has landed). If the image is still rendering this row
      // simply holds as the working stage until it arrives.
      if (checklistHandle) checklistHandle.advanceTo(3);
      maybeReveal();
    }

    // -- trace RAF -----------------------------------------------------------
    const tick = (now) => {
      if (stale()) return;
      const rawP = clamp((now - t0) / traceMs, 0, 1);
      // FEEDBACK item 2: when ports drive the easing, the time-warp redistributes the
      // SAME total duration so the head slows at each port; otherwise the plain trace
      // curve (linear body + eased finish) is used unchanged.
      const drawnP = portWarp ? portWarp(rawP) : traceProgress(rawP, cfg.easeFinalFrom);
      const drawnLen = drawnP * totalLen;
      // Drop each port marker the moment the trace head reaches it (FEEDBACK item 2).
      for (const m of markers) {
        if (!m.revealed && drawnLen >= m.s) {
          m.revealed = true;
          m.el.style.opacity = "1";
          m.el.animate(
            [
              { transform: "scale(0)", opacity: 0 },
              { transform: "scale(1.25)", opacity: 1, offset: 0.7 },
              { transform: "scale(1)", opacity: 1 },
            ],
            { duration: cfg.portMarkerRevealMs, easing: "ease-out", fill: "both" }
          );
        }
      }
      // SEQUENTIAL reveal: drive each subpath's dashoffset from the shared
      // cumulative drawn length. Subpath i only begins once all earlier
      // subpaths are fully drawn (drawnLen passes its cumulative start `start`).
      for (const sp of routePaths) {
        let off;
        if (drawnLen >= sp.start + sp.len) off = 0;            // fully drawn
        else if (drawnLen <= sp.start) off = sp.len;           // hidden
        else off = sp.len - (drawnLen - sp.start);             // partial
        sp.el.style.strokeDashoffset = String(off);
      }
      // Ship-dot rides the head of the active subpath; it naturally jumps
      // edge→edge at the seam as the route crosses the dateline.
      let active = routePaths[routePaths.length - 1];
      let localLen = active.len; // default: end of last subpath
      for (const sp of routePaths) {
        if (drawnLen < sp.start + sp.len) {
          active = sp;
          localLen = drawnLen - sp.start;
          break;
        }
      }
      const hp = active.el.getPointAtLength(clamp(localLen, 0, active.len));
      head.setAttribute("cx", String(hp.x));
      head.setAttribute("cy", String(hp.y));
      // Skeleton fade-in phase → "Building your map" stage. The coastline itself is
      // skipped when it's off (no paths), but the stage still advances so the
      // checklist stays coherent (route → map → ready) either way.
      if (!skeletonShown && rawP >= cfg.skeletonFadeAt) {
        skeletonShown = true;
        if (skelPaths.length) skelGroup.style.opacity = "1";
        if (checklistHandle) checklistHandle.advanceTo(2);
        // "Building your map" beat → start writing the frame fields in, one after another.
        startFieldStream(token);
      }
      if (rawP < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        traceDone = true;
        raf = 0;
        onTraceEnd();
      }
    };
    raf = requestAnimationFrame(tick);

    // -- poll the job --------------------------------------------------------
    // Task 2 — cap consecutive failed poll GETs. Without a cap, a render service that is down or has
    // LOST the job (never returns ready/failed) spins the checklist forever. Count CONSECUTIVE
    // failures; a good GET resets it. On exhaustion, fail the SAME way a `status: "failed"` poll does:
    // freeze in place and report { outcome:"terminal", reason:"render-failed" } via the API (failFrozen).
    let pollErrors = 0;
    const poll = async () => {
      if (stale()) return;
      // If the sync response already carried a ready imageUrl (cached on the
      // server), short-circuit the poll.
      if (syncImageUrl) {
        imageReady = true;
        imageUrl = syncImageUrl;
        maybeReveal();
        return;
      }
      let pd;
      try {
        const res = await http.get(`/voyage/preview/${jobId}`);
        pd = res.data;
      } catch {
        if (stale()) return;
        if (++pollErrors > cfg.pollMaxRetries) {
          // Cap exhausted → freeze in place and report a terminal failure via the API (no on-screen copy).
          return failFrozen(settle, { outcome: "terminal", reason: "render-failed" });
        }
        pollTimer = setTimeout(poll, cfg.pollMs);
        return;
      }
      pollErrors = 0; // a successful GET clears the streak — only a SUSTAINED outage trips the cap
      if (stale()) return;
      if (pd.labels) labels = pd.labels; // D7: carry label status (incl. "unavailable") to the reveal
      if (pd.status === "failed") {
        return failFrozen(settle, { outcome: "terminal", reason: "render-failed" });
      }
      if (pd.status === "ready" && pd.imageUrl) {
        imageReady = true;
        imageUrl = pd.imageUrl;
        maybeReveal();
        return;
      }
      pollTimer = setTimeout(poll, cfg.pollMs);
    };
    poll();
  }

  // ---- failure = freeze + stay silent (owner decision 2026-07-03) ----------
  // On ANY data/render failure the widget FREEZES: it stops the loading chrome (so nothing keeps
  // animating as if still working) but paints NO copy and clears NO rendered content — the last
  // frame is left in place. The failure is reported ONLY through the event/return API
  // ({ outcome, reason }); the storefront owns all failure UX (e.g. triggering clear() / a reset).
  function failFrozen(settle, result) {
    clearAnimChrome();
    if (pulseAnim) { try { pulseAnim.cancel(); } catch {} pulseAnim = null; }
    teardownChecklist();
    settle?.(result);
  }

  // ===========================================================================
  // PUBLIC API (public_api_v3.md §3)
  // ===========================================================================

  /**
   * Render a fresh inset for `current` (voyage identity + style/size/layout), with animation.
   * Driven by run(). On a cold first paint it mounts the BUNDLED default frame so a framed shell
   * is visible immediately; the real frame from the API response then replaces it (D14, brief §4).
   */
  async function renderInset(cfg) {
    const token = ++runToken;
    cancelled = false;
    clearAnimChrome();
    teardownChecklist();
    // Arm the build-phase field stream: from the moment the frame mounts, streamable fields read
    // as skeletons (even if their value is already seeded) until the "Building your map" window
    // writes each one in. Reset to 0 so a re-run replays the write. (Title is never streamed.)
    cancelFieldStream();
    streamActive = true;
    for (const k of STREAM_FIELDS) fieldStream[k] = 0;

    let settle;
    const settled = new Promise((r) => (settle = r));
    const stale = () => cancelled || token !== runToken;
    // Latest-wins closure: settle the predecessor this run supersedes (a new run() reaches here
    // without going through supersedeRun), then take the slot — so every `if (stale()) return` /
    // `return settled` supersede exit below is guaranteed a settled promise, resolved by whoever
    // superseded us.
    if (settleActiveRun) settleActiveRun(supersededResult());
    settleActiveRun = settle;

    // ---- STAGE 0: "Fetching your data" -------------------------------------
    // Show the floating checklist immediately and hold a minimum 2s beat BEFORE
    // anything maps-related appears. The real sync POST runs concurrently (the API
    // is fast); we gate the route start on BOTH the response and the fetch floor so
    // the AI data-fetch always reads as deliberate work. On a cold first load there
    // is no frame yet, so we paint the BUNDLED default frame (brief §3/§4 / D14) for an
    // instant framed shell — skeleton fields, empty inset — until the API frame lands.
    ensureShell();
    if (!activeFrame) {
      // Cold first paint with no active frame. The bundled default is the STANDARD product's
      // instant-first-paint shell ONLY — never a fallback for a branded product (§1d/§3), where
      // painting the Standard shell would be visibly wrong. With the D15 flow setFrame runs before
      // run(), so this branch is normally only reached for the plain Standard default mount.
      const st = String(current.style ?? "").trim().toLowerCase();
      const styleIsStandard = st === "" || st === "standard" || st === "none";
      const fallback = styleIsStandard ? bundledDefaultFrame(current.layout) : null;
      if (fallback) {
        // Instant first paint: a real framed shell from the bundled default (replaced by the
        // API's frame on response). shipRevealed stays false so the ship slot reads as a skeleton.
        mountFrame(fallback);
      } else {
        // Bundled default unavailable (shouldn't happen) — fall back to a plain inset-toned shell,
        // with an aspect matching the requested orientation so it isn't a portrait flash.
        shell.replaceChildren();
        const land = current.layout === "Landscape";
        shell.style.aspectRatio = land ? "4 / 3" : "3 / 4";
        shell.style.width = land
          ? "min(100cqw, calc(100cqh * (4 / 3)))"
          : "min(100cqw, calc(100cqh * (3 / 4)))";
        shell.style.height = "auto";
        const bg = document.createElement("div");
        Object.assign(bg.style, { position: "absolute", inset: "0", background: baseOpts.insetBgColor });
        shell.appendChild(bg);
      }
    }
    if (cfg.checklist) {
      checklistHandle = makeChecklist(cfg);
      shell.appendChild(checklistHandle.el);
      positionChecklist(cfg);
      checklistHandle.advanceTo(0);
      // next frame → fade/slide in
      requestAnimationFrame(() => { if (!stale() && checklistHandle) checklistHandle.fadeIn(); });
    }

    const fetchFloor = new Promise((r) => setTimeout(r, cfg.fetchMinMs));

    let sync;
    try {
      // public_api_v3.md §1: the five voyage-identity fields + the Shopify style/size identifiers +
      // the customer's orientation (`layout`), passed straight through. No `voyageId`, no mapStyle/
      // size model. Path is relative to apiBaseUrl (the `/api/v3` base).
      const res = await http.post("/voyage/preview", {
        ...current.identity, // ship_name, start_port, end_port, start_date, end_date
        style: current.style,
        height: current.height,
        width: current.width,
        layout: current.layout,
        coastline: cfg.coastline, // skip the coastline skeleton payload when off
      });
      sync = res.data;
    } catch {
      // Task 4a — supersession guard FIRST (mirrors the success path's `token !== runToken` check
      // below). A superseded run whose POST rejects LATE must NOT touch the newer run (D15: "stale
      // never paints over newer"). Return the already-settled sentinel with NO teardown, NO event.
      if (token !== runToken) return settled; // superseded — leave the newer run alone
      // network/server error → freeze in place and report terminal via the API (no on-screen copy).
      failFrozen(settle, { outcome: "terminal", reason: "render-failed" });
      return settled;
    }
    if (token !== runToken) return settled; // superseded

    // Hold out the remainder of the minimum fetch beat before revealing the route.
    await fetchFloor;
    if (token !== runToken) return settled; // superseded during the hold

    // bad case: server says no voyage (exists:false). Freeze in place and report via the API — the
    // widget paints NO copy and rebuilds NO shell; the storefront owns the "not found / buy now" UX.
    if (!sync || sync.exists === false) {
      failFrozen(settle, { outcome: "bad-case", reason: "voyage-not-found" });
      return settled;
    }

    // Server-driven frame text — auto-fill from the response, exactly like mileage (C6). Ship
    // name, dates and duration are FACTS of the voyage (the same values Mapper UI's print
    // renderer draws from the voyage row); the store never supplies them. Only `title` is
    // store-editable (via setFields). Dates arrive as ISO strings, duration as an integer number
    // of days — produceDisplayDateRange / pluraliseDays format them to match the print.
    distance = sync.distance;
    fields.shipName = sync.shipName;
    fields.startDate = sync.startDate;
    fields.endDate = sync.endDate;
    fields.duration = sync.duration;

    // Mount the frame from the response, re-render persisted field values onto it,
    // then animate the inset. mountFrame() wipes the shell, so re-attach the
    // checklist over the freshly-mounted inset and reposition it.
    mountFrame(sync.frame);
    if (checklistHandle) {
      shell.appendChild(checklistHandle.el);
      positionChecklist(cfg);
    }
    animateInset(sync, cfg, (result) => settle(result), token);

    return settled;
  }

  // ---- neutral "preview unavailable" overlay (whole-call frames failure, §1d) ----
  // Painted only when /prints/frames fails entirely AND no frame is up yet. Deliberately NOT a
  // branded shell and NOT the bundled Standard default — for a branded product a wrong shell is
  // worse than an honest "unavailable". Cleared once recipes load (or a frame is otherwise up).
  // (showUnavailable/clearUnavailable removed 2026-07-03 — the widget paints no failure/unavailable
  // copy. A mount-time whole-call failure is reported via `rendererror { stage:"frames" }` and leaves
  // the container untouched for the storefront to handle.)

  /**
   * MOUNT-TIME PRELOAD (D15 / public_api_v3.md §1d/§3). Fetch every product variant's frame
   * recipe ONCE, key them by the raw variant string, then emit `ready` with which resolved /
   * which didn't (Reading A — the widget fetches its own recipes). No further network is needed
   * for frame switching. On a WHOLE-call failure show a neutral "unavailable" state and retry —
   * NEVER paint the bundled Standard default for a branded product (§1d).
   */
  async function loadRecipes() {
    const variants = Array.isArray(baseOpts.variants) ? baseOpts.variants : [];
    if (!variants.length) {
      // Nothing to preload (e.g. the plain Standard default mount): ready with an empty set.
      recipesLoaded = true;
      emit("ready", { resolved: [], unresolved: [] });
      settleRecipesGate();
      return;
    }
    const params = new URLSearchParams();
    params.set("style", baseOpts.style ?? "Standard");
    for (const v of variants) params.append("variant", v);
    try {
      const res = await http.get(`/prints/frames?${params.toString()}`);
      if (destroyed) return; // a late response must not resurrect a destroyed instance
      const data = res.data || {};
      frameRecipes.clear();
      for (const [variant, frame] of Object.entries(data.frames || {})) frameRecipes.set(variant, frame);
      unresolvedVariants = Array.isArray(data.unresolved) ? data.unresolved : [];
      if (data.style) resolvedStyle = data.style;
      recipesLoaded = true;
      framesRetries = 0;
      // Honour a setFrame() that arrived before the recipes were ready — BEFORE emitting `ready`,
      // so ready handlers (and the parked runs released below) always see the pending frame applied
      // and can never be stomped or superseded by a stale replay.
      if (pendingFrameVariant != null) {
        const v = pendingFrameVariant;
        pendingFrameVariant = null;
        setFrame(v);
      }
      emit("ready", { resolved: Array.from(frameRecipes.keys()), unresolved: unresolvedVariants });
      settleRecipesGate();
    } catch {
      if (destroyed) return;
      if (framesRetries < baseOpts.framesMaxRetries) {
        framesRetries++;
        framesRetryTimer = setTimeout(loadRecipes, baseOpts.framesRetryMs);
      } else {
        // Frames could not be loaded. Report via the API (no on-screen copy); the container is left
        // untouched for the storefront to handle. Settle the gate so parked runs refuse (they
        // re-check recipesLoaded) instead of waiting forever.
        emit("rendererror", { stage: "frames", reason: "frames-unavailable" });
        settleRecipesGate();
      }
    }
  }

  /**
   * setFrame(variantTitle) — LEVER A (D15): paint the branded shell for a preloaded variant
   * INSTANTLY, with NO render and NO network. A pure in-memory lookup of a recipe fetched on
   * mount. Sets the ACTIVE frame — the single source of truth for the size/style/layout any
   * subsequent run() will render into, so the painted shell and the rendered map can never
   * disagree. The customer's own field text (title etc.) persists; only map-derived values
   * (mileage, the real ship) reset until a run() fills them.
   *
   * Called before recipes have loaded → remembered and applied on load. For an unresolved /
   * unknown variant → emits `framefailed` (never paints a wrong shell).
   */
  function setFrame(variantTitle) {
    // Latest-wins: supersede any in-flight run and stop its chrome. The facts reset stays LOCAL
    // (below, after the guards) rather than in supersedeRun, so the guard-return paths keep the
    // current field values instead of clearing them to skeletons. A run PARKED on the recipes gate
    // is spared (abandonParkedRun: false): it renders whatever frame is newest when it resumes.
    supersedeRun({ abandonParkedRun: false });

    if (!recipesLoaded) { pendingFrameVariant = variantTitle; return; }

    const recipe = frameRecipes.get(variantTitle);
    if (!recipe) { emit("framefailed", { variant: variantTitle }); return; }

    activeVariant = variantTitle;
    // The active frame drives the NEXT run(): size + layout come from here (D15). `style` stays the
    // RAW `_style` slug (fixed per product page) — the widget forwards it verbatim and the server
    // normalises (D14). It must NOT be the endpoint's normalised echo: `format_style` isn't
    // idempotent (e.g. "Midnight Blue" → "Midnight blue"), so re-sending the echo would drift.
    current.height = recipe.height;
    current.width = recipe.width;
    current.layout = recipe.orientation;

    // All four server-driven facts clear together until the next run() re-fetches them, so a size
    // change shows the instant branded shell with skeleton text (not stale values), then fills in.
    distance = undefined;   // mileage is map-derived — cleared until a run() re-renders it
    fields.shipName = undefined;
    fields.startDate = undefined;
    fields.endDate = undefined;
    fields.duration = undefined;
    shipRevealed = false;   // ship slot returns to the skeleton until the morph reveal
    cancelled = false;      // mountFrame defers a font recompose gated on !cancelled
    // Clone so mountFrame's coBranding preload / in-place tweaks never mutate the cached recipe
    // (the same recipe is reused every time this variant is re-selected).
    mountFrame(JSON.parse(JSON.stringify(recipe)));
  }

  /**
   * run(voyageIdentity) / run() — LEVER B (D15): render the map into the ACTIVE frame
   * (set by setFrame). run() NEVER takes its own size/style/layout — it uses the active frame,
   * so shell and map can't disagree. run(details) remembers the details; bare run() re-renders
   * the remembered ones (the post-size-change path). Shopify decides WHEN to call this and owns
   * debounce; the widget owns latest-wins (a new run supersedes any in-flight one).
   *   voyageIdentity: { ship_name, start_port, end_port, start_date, end_date }
   * Resolves when the experience settles (and emits the matching status event). On failure the widget
   * FREEZES in place and paints NOTHING — the resolved result / `rendererror` payload is the
   * storefront's ONLY failure signal (it owns all failure UX). Shapes:
   *   { outcome: "revealed",  imageUrl, labels }               → `rendercomplete`  (labels = D7 status)
   *   { outcome: "bad-case",  reason: "voyage-not-found" }     → `rendererror`
   *   { outcome: "terminal",  reason: "render-failed" }        → `rendererror`  (render failed/late)
   *   { outcome: "terminal",  reason: "no-frame"|"no-identity", message } → `rendererror`  (API misuse)
   *   { outcome: "terminal",  reason: "frames-unavailable", message }     → `rendererror`  (recipes never loaded)
   *   { outcome: "superseded" } — NO event: the run (in-flight or parked on the recipes gate) was
   *   superseded by a newer run()/setFrame()/clear()/renderFinal()/destroy(), which owns all events
   *   from there. Every run() resolves — a superseded run never leaves its caller awaiting forever.
   *   A run() while the setFrame frame is still pending recipe load PARKS until recipes settle, then
   *   renders the real frame geometry — it never paints or POSTs the baked-in default (the
   *   portrait-flash bug).
   */
  async function run(voyageIdentity) {
    if (voyageIdentity !== undefined && voyageIdentity !== null) lastIdentity = voyageIdentity;
    const identity = lastIdentity;
    if (!identity) {
      const result = { outcome: "terminal", reason: "no-identity",
        message: "run(): no voyage details yet — call run(voyageIdentity) first" };
      emit("rendererror", result);
      return result;
    }
    // Latest-wins across run() calls (this ticket outlives the park below; renderInset's own
    // runToken can't cover the parked window because it only advances once a render starts).
    const ticket = ++runCallToken;

    // The selected frame is still pending recipe load: its geometry (size/layout) is unknown, and
    // current.* still holds the mount defaults. Park until loadRecipes settles — it applies the
    // pending frame before releasing the gate — then proceed with the real geometry.
    if (!recipesLoaded && pendingFrameVariant != null) {
      await recipesGate;
      if (destroyed || ticket !== runCallToken) return supersededResult(); // abandoned while parked
      if (!recipesLoaded) {
        // Frames terminally failed to load (or apiBaseUrl was never configured) — refuse like the
        // mount-time frames error rather than render the default geometry.
        const result = { outcome: "terminal", reason: "frames-unavailable",
          message: "run(): frame recipes could not be loaded — the render was refused" };
        emit("rendererror", result);
        return result;
      }
    }

    // OWNER DECISION (Task 4b): run() with NO active/pending frame is REFUSED, not queued. Without a
    // frame the render would POST the baked-in default 40×30 Portrait — a size the product may not
    // even offer. A frame is "selected" once setFrame() has run (activeVariant/activeFrame set) or is
    // pending recipe load (pendingFrameVariant). If none, refuse: emit rendererror and do nothing
    // else — no renderstart, no POST. The storefront must call setFrame(variant) before run().
    // (After the park above this also catches a pending variant that resolved as unknown/unresolved —
    // the replay emitted `framefailed` and left no frame selected.)
    const hasFrame =
      activeFrame != null || activeVariant != null || pendingFrameVariant != null;
    if (!hasFrame) {
      const result = { outcome: "terminal", reason: "no-frame",
        message: "run(): no frame selected — call setFrame(variant) before run()" };
      emit("rendererror", result);
      return result;
    }
    const cfg = { ...baseOpts };
    cancelled = false;
    shipRevealed = false; // fresh voyage → ship slot starts as a skeleton (revealed at the morph)
    current.identity = identity;
    // style/height/width/layout are already set on `current` by setFrame (single source of truth).

    emit("renderstart");
    const result = await renderInset(cfg);
    if (result && result.outcome === "revealed") emit("rendercomplete", result);
    else if (result && result.outcome !== "superseded") emit("rendererror", result);
    return result; // superseded → no event (the superseding call owns them)
  }

  /**
   * LIVE BINDING — set the custom map `title`; the frame updates IMMEDIATELY.
   * `title` is the ONLY editable frame text. Ship name, dates, duration and
   * distance are all server-driven voyage FACTS set from the preview response —
   * they are deliberately NOT settable here (the store cannot supply or override
   * them). Other keys passed in are ignored.
   */
  function setFields(next = {}) {
    if (next.title === undefined) return false;
    fields.title = next.title;
    if (activeFrame) composeFrame();
    return true;
  }

  // NOTE (brief §4 / public_api_v3.md §3): orientation is an INPUT passed via run(...).layout, not
  // a control. There is deliberately NO setOrientation toggle — the prototype's live-flip + dual-
  // orientation client cache are removed. A different orientation is simply a fresh run(...).

  /**
   * FINAL framed product (composite path) — mount the finished product with NO
   * animation and NO network/API call, for server-side screenshotting. Reuses
   * the live frame path (ensureShell → mountFrame → composeFrame) so layout/text
   * match the live frame exactly, then drops the already-baked inset PNG into the
   * inset box (the route is in the PNG, so no route/skeleton/port overlays). Fields
   * apply via the same path setFields uses; unset fields stay skeleton placeholders.
   *
   * Resolves only once brand fonts are ready AND the inset <img> has loaded, so a
   * screenshot taken right after resolve captures a fully-rendered product.
   * Standalone: callable straight after createVoyagePreview, without run().
   *
   * Concealment (D11): receives only a frame config, an image URL, and the
   * customer's own field text — never lat/lng.
   */
  function renderFinal({ frame, insetImageUrl, fields: nextFields, insetDimensions, distance: nextDistance } = {}) {
    if (!frame) throw new Error("renderFinal: `frame` config is required");
    // supersede any in-flight animated run / flip and stop its chrome. streamActive drops (via
    // supersedeRun) because the final product paints full text at once, with no write wipe.
    supersedeRun();
    shipRevealed = true; // finished product → real ship, not the Arvia skeleton
    finalMode = true; // suppress skeleton placeholders for this (single-use) final render

    // Apply fields via the same normalisation setFields uses; distance is
    // server-driven so it isn't a setFields key — set it directly if supplied.
    // It may arrive as a top-level `distance` (the composite backend threads the
    // voyage's numNMiles through here) or nested in `fields` — accept either, and
    // apply it to `distance` exactly as the live frame does (composeFrame renders
    // the mileage line from this var; see the imageDistanceDisplayed block).
    if (nextFields) {
      for (const k of Object.keys(fields)) {
        if (nextFields[k] !== undefined) fields[k] = nextFields[k];
      }
      if (nextFields.distance !== undefined) distance = nextFields.distance;
    }
    if (nextDistance !== undefined && nextDistance !== null) distance = nextDistance;

    // Mount the frame exactly as the live path does (no animation). mountFrame
    // calls ensureShell + composeFrame; cancelled=true would skip its deferred
    // font recompose, so clear it for this synchronous mount and re-run compose.
    cancelled = false;
    mountFrame(frame);
    if (insetDimensions) activeInsetDims = insetDimensions;

    // Place the baked inset PNG in the inset box (same element/styling as the
    // morph-reveal / cached-restore img: fills the inset, opaque, no overlays).
    const img = document.createElement("img");
    Object.assign(img.style, {
      position: "absolute", inset: "0", width: "100%", height: "100%",
      objectFit: "fill", opacity: "1", pointerEvents: "none",
    });
    insetImg = img;
    insetBox.appendChild(img);

    // The coBranding loads kicked off by mountFrame above. Idempotent — preloadAsset returns the
    // existing promise per file — so this just gets a handle on them to await below.
    const brandingReady = preloadStyleAssets(frame.layout && frame.layout.coBranding);

    // Resolve only once the brand fonts are ready, the inset image has finished loading, AND
    // every coBranding asset has loaded and been composed — so a screenshot right after resolve
    // is complete. The logos are NOT optional here: the composite driver screenshots the moment
    // this resolves, uploads the result and mapper-api caches it permanently under the composite
    // key, so a logo that loses the race becomes a cached, purchasable product missing its
    // partner branding. A hung asset stalls this promise and the job times out — failing loudly
    // is the correct outcome, matching the driver's existing blank-inset guard.
    const imgReady = new Promise((resolve) => {
      const done = () => resolve();
      img.onload = () => {
        if (img.decode) img.decode().then(done, done);
        else done();
      };
      img.onerror = done; // don't hang the screenshot on a missing inset
    });
    img.src = insetImageUrl;
    return Promise.all([brandFontsReady, imgReady, brandingReady]).then(() => undefined);
  }

  /** Blank framed shell: border/margins/detail strip visible, skeletons, empty inset. */
  function clear() {
    // Supersede + reset the server facts back to skeleton placeholders. clear() additionally wipes
    // the host-editable title (supersedeRun leaves title alone), so a fully blank shell is shown.
    supersedeRun({ resetFacts: true });
    fields.title = undefined;
    shipRevealed = false; // ship slot returns to the Arvia skeleton on clear
    if (activeFrame) {
      // re-mount the same frame layout, but empty inset + skeleton fields.
      mountFrame(activeFrame);
    } else if (shell) {
      shell.replaceChildren();
    }
  }

  function destroy() {
    // Supersede without the field-stream reset: destroy never touched the stream (the cancelled /
    // runToken bump above already neutralises any pending tick) and is about to null the DOM out.
    supersedeRun({ resetFieldStream: false });
    // Release any run parked on the recipes gate (it sees `destroyed` and resolves undefined) —
    // otherwise a destroy while frames are still loading would leave its promise pending forever.
    destroyed = true;
    settleRecipesGate();
    if (framesRetryTimer) { clearTimeout(framesRetryTimer); framesRetryTimer = 0; }
    if (vpRoot) {
      vpRoot.remove();
      vpRoot = null;
      shell = null;
    } else if (shell) {
      shell.remove();
      shell = null;
    }
    frameSvg = insetBox = insetSvg = insetImg = null;
    activeFrame = activeInsetDims = null;
  }

  // Kick off the one-time recipe preload (D15). Fire-and-forget: it emits `ready` when done, or on a
  // whole-call failure emits `rendererror { stage:"frames" }` and retries — it paints nothing. If
  // apiBaseUrl was never configured (Task 4c) we skip the network entirely (the console.error above
  // is the only signal); no localhost/default call is ever made and the container is left untouched —
  // but the recipes gate still settles, so a parked run() refuses instead of waiting forever.
  if (apiConfigured) loadRecipes();
  else settleRecipesGate();

  return { run, setFrame, setFields, renderFinal, clear, destroy, on };
}
